/**
 * Make / Model / Year filters for the inventory page.
 *
 * Everything here is derived from the loaded units, so it works for any
 * dealership: no make, model, category or year is hard-coded.
 *
 * Canonical keys
 * - A make key is the make lower-cased with everything that is not a letter or
 *   digit removed: "JOHN DEERE", "John  Deere" -> "johndeere"; "Case-IH",
 *   "CASE IH" -> "caseih"; "Flexi Coil", "FLEXI-COIL" -> "flexicoil".
 * - A model key is built the same way, and a model is identified within its
 *   make: "<makeKey>|<modelKey>", so "S 790" and "S790" of John Deere are one
 *   model while Case IH "S790" stays separate.
 * - Blank values get UNKNOWN_KEY ("~"), shown as "Unknown make"/"Unknown model".
 * - Keys are idempotent (key(key(x)) === key(x)), so a key can be written into
 *   the URL wherever a label would not survive the round trip.
 *
 * Years: YEAR_MANUF; null, 0, non-numeric or <= 1900 counts as "no year".
 */

export const UNKNOWN_KEY = '~';
export const UNKNOWN_MAKE_LABEL = 'Unknown make';
export const UNKNOWN_MODEL_LABEL = 'Unknown model';
export const UNCATEGORISED_LABEL = 'Uncategorised';
export const MACHINE_URL_PARAMS = ['make', 'model', 'year', 'noyear'];

export const MACHINE_FILTER_DEFAULTS = Object.freeze({
    makes: [],
    models: [],
    yearFrom: null,
    yearTo: null,
    includeNoYear: true,
});

const ID_SEPARATOR = '|';
const NON_ALNUM = /[^\p{L}\p{N}]+/gu;

const squash = (value) => String(value ?? '').toLowerCase().replace(NON_ALNUM, '');
const collapse = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

export function canonicalMakeKey(raw) {
    return squash(raw) || UNKNOWN_KEY;
}

export function canonicalModelKey(raw) {
    return squash(raw) || UNKNOWN_KEY;
}

/** Filter id of a model: canonical make + canonical model. */
export function modelFilterId(make, model) {
    return `${canonicalMakeKey(make)}${ID_SEPARATOR}${canonicalModelKey(model)}`;
}

const makeKeyOfModelId = (id) => String(id).split(ID_SEPARATOR)[0];

export function parseYear(raw) {
    if (raw == null || raw === '') return null;
    const year = Number(raw);
    if (!Number.isFinite(year)) return null;
    const rounded = Math.round(year);
    return rounded > 1900 ? rounded : null;
}

// ── Labels ───────────────────────────────────────────────────────────────

const hasLower = (s) => s !== s.toUpperCase();
const hasUpper = (s) => s !== s.toLowerCase();
const isMixedCase = (s) => hasLower(s) && hasUpper(s);

/** Most frequent entry of a Map<string, count>; ties go to the first seen. */
function mostFrequent(variants, predicate = () => true) {
    let best = null;
    let bestCount = -1;
    variants.forEach((count, value) => {
        if (predicate(value) && count > bestCount) {
            best = value;
            bestCount = count;
        }
    });
    return best;
}

// Brand acronyms of four or more letters that title case would spoil. Kept
// deliberately tiny: anything else of 4+ letters reads better title-cased
// (FORD -> Ford, TORO -> Toro, MISC -> Misc), and a dealer who writes a brand
// in mixed case anywhere in the data gets that spelling regardless.
const UPPERCASE_BRANDS = new Set(['AGCO']);

/**
 * Title-case an all-caps or all-lower-case name, keeping short acronyms in
 * upper case: words with digits, words of one or two letters ("IH"), and
 * three-letter words that are either the whole name ("DJI", "MDS") or have
 * no vowel ("JCB"). Words of four letters or more are title-cased unless
 * allow-listed above.
 */
function smartTitleCase(name) {
    const words = name.match(/[\p{L}\p{N}]+/gu) || [];
    const single = words.length === 1;
    return name.replace(/[\p{L}\p{N}]+/gu, (word) => {
        const upper = word.toUpperCase();
        if (UPPERCASE_BRANDS.has(upper)) return upper;
        if (/\p{N}/u.test(word) || word.length <= 2) return upper;
        if (word.length === 3 && (single || !/[AEIOU]/.test(upper))) return upper;
        return upper.charAt(0) + word.slice(1).toLowerCase();
    });
}

/**
 * Display form of a model name, by the make-label rule applied cautiously:
 * a name written in mixed case is kept as written; otherwise only plain
 * all-caps words longer than three letters, with no digits and with a vowel,
 * are title-cased ("BUCKET" → "Bucket", "STRAWMASTER" → "Strawmaster").
 * Codes and short words stay as they come ("S790", "C16F", "AP11F", "HD",
 * "SDX-102"), as do the allow-listed brand acronyms.
 */
export function modelDisplayLabel(model) {
    const text = String(model ?? '');
    if (isMixedCase(text)) return text;
    return text.replace(/[\p{L}\p{N}]+/gu, (word) => {
        if (!/^\p{Lu}{4,}$/u.test(word) || !/[AEIOUY]/.test(word) || UPPERCASE_BRANDS.has(word)) return word;
        return word.charAt(0) + word.slice(1).toLowerCase();
    });
}

/**
 * Display label for a make from its spellings in the data (Map<spelling, count>).
 * The most frequent mixed-case spelling wins, since a dealer who typed
 * "Case IH" meant that casing; otherwise the most frequent spelling is
 * title-cased.
 */
export function makeDisplayLabel(variants) {
    const mixed = mostFrequent(variants, isMixedCase);
    if (mixed) return mixed;
    const top = mostFrequent(variants);
    return top ? smartTitleCase(top) : UNKNOWN_MAKE_LABEL;
}

// ── Per-unit keys (cached) ───────────────────────────────────────────────

const unitCache = new WeakMap();

/** Canonical make/model keys and parsed year of a unit, cached per object. */
export function unitMachine(unit) {
    let entry = unitCache.get(unit);
    if (!entry) {
        const makeKey = canonicalMakeKey(unit?.MAKE);
        const modelKey = canonicalModelKey(unit?.MODEL);
        entry = {
            makeKey,
            modelId: `${makeKey}${ID_SEPARATOR}${modelKey}`,
            year: parseYear(unit?.YEAR_MANUF),
        };
        if (unit && typeof unit === 'object') unitCache.set(unit, entry);
    }
    return entry;
}

const bump = (map, key, by = 1) => map.set(key, (map.get(key) || 0) + by);

/**
 * Everything the panel lists, built once per inventory load:
 * makes: Map<makeKey, {key, label, total}>
 * models: Map<modelId, {id, makeKey, makeLabel, label, category, total}>
 * years: distinct real years, newest first; noYearCount: units without one.
 */
export function buildMachineCatalog(units) {
    const makeSpellings = new Map();
    const modelSpellings = new Map();
    const modelCategories = new Map();
    const makes = new Map();
    const models = new Map();
    const yearSet = new Set();
    let noYearCount = 0;

    (units || []).forEach((u) => {
        const { makeKey, modelId, year } = unitMachine(u);
        if (!makeSpellings.has(makeKey)) makeSpellings.set(makeKey, new Map());
        if (!modelSpellings.has(modelId)) modelSpellings.set(modelId, new Map());
        if (!modelCategories.has(modelId)) modelCategories.set(modelId, new Map());
        const make = collapse(u?.MAKE);
        const model = collapse(u?.MODEL);
        if (make) bump(makeSpellings.get(makeKey), make);
        if (model) bump(modelSpellings.get(modelId), model);
        // Blank categories say nothing about where a model belongs.
        const category = collapse(u?.CATEGORY);
        if (category) bump(modelCategories.get(modelId), category);
        if (year == null) noYearCount += 1;
        else yearSet.add(year);
    });

    makeSpellings.forEach((variants, key) => {
        makes.set(key, {
            key,
            label: key === UNKNOWN_KEY ? UNKNOWN_MAKE_LABEL : makeDisplayLabel(variants),
            total: 0,
        });
    });
    (units || []).forEach((u) => {
        const { makeKey, modelId } = unitMachine(u);
        makes.get(makeKey).total += 1;
        let model = models.get(modelId);
        if (!model) {
            const label = mostFrequent(modelSpellings.get(modelId)) || UNKNOWN_MODEL_LABEL;
            model = {
                id: modelId,
                makeKey,
                makeLabel: makes.get(makeKey).label,
                label,
                category: mostFrequent(modelCategories.get(modelId)) || '',
                total: 0,
            };
            models.set(modelId, model);
        }
        model.total += 1;
    });

    return {
        makes,
        models,
        years: [...yearSet].sort((a, b) => b - a),
        noYearCount,
    };
}

// ── Matching ─────────────────────────────────────────────────────────────

const listOf = (value) => (Array.isArray(value) ? value : []);

export function hasYearFilter(filters) {
    return filters?.yearFrom != null || filters?.yearTo != null || filters?.includeNoYear === false;
}

export function countActiveMachineFilters(filters) {
    return (listOf(filters?.makes).length > 0 ? 1 : 0)
        + (listOf(filters?.models).length > 0 ? 1 : 0)
        + (hasYearFilter(filters) ? 1 : 0);
}

function yearPasses(year, filters) {
    if (year == null) return filters?.includeNoYear !== false;
    if (filters?.yearFrom != null && year < filters.yearFrom) return false;
    if (filters?.yearTo != null && year > filters.yearTo) return false;
    return true;
}

/**
 * Whether a unit passes the machine filters. `skip` ('make' | 'model' |
 * 'year') leaves that one out, which is how each facet gets its counts.
 */
export function matchesMachineFilters(unit, filters, skip = null) {
    const { makeKey, modelId, year } = unitMachine(unit);
    const makes = listOf(filters?.makes);
    const models = listOf(filters?.models);
    if (skip !== 'make' && makes.length > 0 && !makes.includes(makeKey)) return false;
    if (skip !== 'model' && models.length > 0 && !models.includes(modelId)) return false;
    if (skip !== 'year' && !yearPasses(year, filters)) return false;
    return true;
}

/**
 * Faceted counts in one pass over `baseUnits` (the units that pass every
 * other filter on the page). Each facet's counts apply the other two machine
 * filters but not its own.
 */
export function computeMachineFacets(baseUnits, filters) {
    const makeSet = new Set(listOf(filters?.makes));
    const modelSet = new Set(listOf(filters?.models));
    const makeCounts = new Map();
    const modelCounts = new Map();
    const yearCounts = new Map();
    let noYearCount = 0;

    (baseUnits || []).forEach((u) => {
        const { makeKey, modelId, year } = unitMachine(u);
        const makeOk = makeSet.size === 0 || makeSet.has(makeKey);
        const modelOk = modelSet.size === 0 || modelSet.has(modelId);
        const yearOk = yearPasses(year, filters);
        if (modelOk && yearOk) bump(makeCounts, makeKey);
        if (makeOk && yearOk) bump(modelCounts, modelId);
        if (makeOk && modelOk) {
            if (year == null) noYearCount += 1;
            else bump(yearCounts, year);
        }
    });

    return { makeCounts, modelCounts, yearCounts, noYearCount };
}

// ── Option lists ─────────────────────────────────────────────────────────

const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });

/**
 * Makes: the five with most units under the active filters first (ties by
 * total stock, then A–Z), then the rest A–Z; search narrows both.
 */
export function orderMakeOptions(catalog, makeCounts, query, topN = 5) {
    const all = [...catalog.makes.values()].map((m) => ({ ...m, count: makeCounts.get(m.key) || 0 }));
    const byCommon = (a, b) => b.count - a.count || b.total - a.total || byLabel(a, b);
    const topKeys = new Set([...all].sort(byCommon).slice(0, topN).map((m) => m.key));
    const q = squash(query);
    const matches = (m) => !q || m.key.includes(q) || squash(m.label).includes(q);
    const top = all.filter((m) => topKeys.has(m.key)).sort(byCommon).filter(matches);
    const rest = all.filter((m) => !topKeys.has(m.key)).sort(byLabel).filter(matches);
    return { top, rest };
}

/**
 * Model search. The query is matched against the model text, and — only when
 * the make is shown — against the make text, but never across the joined
 * "Make · Model" string ("S7" must not match Gates "72"). A query of several
 * words also matches when its leading words are in the make and the rest in
 * the model ("deere 8r", "gates 72").
 */
function modelQueryMatcher(query, searchMake) {
    const q = squash(query);
    if (!q) return () => true;
    const words = String(query).trim().split(/\s+/);
    const splits = [];
    if (searchMake) {
        for (let i = 1; i < words.length; i += 1) {
            const makePart = squash(words.slice(0, i).join(' '));
            const modelPart = squash(words.slice(i).join(' '));
            if (makePart && modelPart) splits.push([makePart, modelPart]);
        }
    }
    return (makeLabel, modelLabel) => {
        const model = squash(modelLabel);
        if (model.includes(q)) return true;
        if (!searchMake) return false;
        const make = squash(makeLabel);
        if (make.includes(q)) return true;
        return splits.some(([mk, md]) => make.includes(mk) && model.includes(md));
    };
}

/**
 * Models of the chosen makes (all makes when none is chosen), grouped by
 * category. With exactly one make chosen the label is the model alone,
 * otherwise "Make · Model".
 */
export function groupModelOptions(catalog, selectedMakes, modelCounts, query) {
    const makeSet = new Set(listOf(selectedMakes));
    const showMake = makeSet.size !== 1;
    const matches = modelQueryMatcher(query, showMake);
    const groups = new Map();
    catalog.models.forEach((m) => {
        if (makeSet.size > 0 && !makeSet.has(m.makeKey)) return;
        if (!matches(m.makeLabel, m.label)) return;
        const category = m.category || UNCATEGORISED_LABEL;
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push({
            id: m.id,
            makeKey: m.makeKey,
            makeLabel: m.makeLabel,
            modelLabel: m.label,
            label: showMake ? `${m.makeLabel} · ${m.label}` : m.label,
            count: modelCounts.get(m.id) || 0,
        });
    });
    return [...groups.entries()]
        .sort(([a], [b]) => {
            if (a === UNCATEGORISED_LABEL) return 1;
            if (b === UNCATEGORISED_LABEL) return -1;
            return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })
        .map(([category, options]) => ({ category, options: options.sort(byLabel) }));
}

/** Models still selected after the make selection changed. */
export function pruneModelsForMakes(models, makes) {
    const list = listOf(models);
    const makeSet = new Set(listOf(makes));
    if (makeSet.size === 0) return list;
    return list.filter((id) => makeSet.has(makeKeyOfModelId(id)));
}

/** Make keys and model ids present in `units`. */
export function machineKeySets(units) {
    const makes = new Set();
    const models = new Set();
    (units || []).forEach((u) => {
        const { makeKey, modelId } = unitMachine(u);
        makes.add(makeKey);
        models.add(modelId);
    });
    return { makes, models };
}

/**
 * The filters with makes and models this inventory does not contain left out,
 * so a selection saved for another dealership never empties the table. The
 * input is not modified, and is returned as-is when nothing is dropped.
 */
export function restrictMachineFilters(filters, keySets) {
    const makes = listOf(filters?.makes);
    const models = listOf(filters?.models);
    const keptMakes = makes.filter((key) => keySets.makes.has(key));
    const keptModels = models.filter((id) => keySets.models.has(id));
    if (keptMakes.length === makes.length && keptModels.length === models.length) return filters;
    return { ...filters, makes: keptMakes, models: keptModels };
}

// ── Year ─────────────────────────────────────────────────────────────────

export function yearQuickPicks(currentYear) {
    return [
        { id: 'recent', label: `${currentYear - 3}+`, from: currentYear - 3, to: null },
        { id: 'last5', label: 'Last 5 years', from: currentYear - 4, to: null },
        { id: 'older10', label: 'Older than 10', from: null, to: currentYear - 11 },
    ];
}

export function yearSelectOptions(catalogYears, filters) {
    const set = new Set(catalogYears || []);
    if (filters?.yearFrom != null) set.add(filters.yearFrom);
    if (filters?.yearTo != null) set.add(filters.yearTo);
    return [...set].sort((a, b) => b - a);
}

/**
 * Histogram bars from the oldest to the newest catalog year. Beyond `maxBars`
 * the oldest years fold into one "YYYY or older" bar.
 */
export function yearHistogram(yearCounts, catalogYears, filters, maxBars = 20) {
    if (!catalogYears || catalogYears.length === 0) return [];
    const hi = Math.max(...catalogYears);
    const lo = Math.min(...catalogYears);
    const inRange = (from, to) => (filters?.yearFrom == null || to >= filters.yearFrom)
        && (filters?.yearTo == null || from <= filters.yearTo);
    const bars = [];
    let start = lo;
    if (hi - lo + 1 > maxBars) {
        start = hi - maxBars + 2;
        let count = 0;
        yearCounts.forEach((c, y) => { if (y < start) count += c; });
        bars.push({ label: `${start - 1} or older`, from: lo, to: start - 1, count, inRange: inRange(lo, start - 1) });
    }
    for (let y = start; y <= hi; y += 1) {
        bars.push({ label: String(y), from: y, to: y, count: yearCounts.get(y) || 0, inRange: inRange(y, y) });
    }
    return bars;
}

export function formatYearRange(filters) {
    const { yearFrom: from, yearTo: to } = filters || {};
    let range = '';
    if (from != null && to != null) range = from === to ? String(from) : `${from}–${to}`;
    else if (from != null) range = `${from}+`;
    else if (to != null) range = `up to ${to}`;
    if (filters?.includeNoYear === false) return range ? `${range} · with year` : 'with year only';
    return range || 'All years';
}

// ── Persistence ──────────────────────────────────────────────────────────

const toYear = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isInteger(n) ? n : null;
};

/** The machine-filter keys of saved prefs, coerced to their current shape. */
export function sanitizeMachineFilters(saved) {
    const strings = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
    return {
        makes: strings(saved?.makes),
        models: strings(saved?.models),
        yearFrom: toYear(saved?.yearFrom),
        yearTo: toYear(saved?.yearTo),
        includeNoYear: typeof saved?.includeNoYear === 'boolean' ? saved.includeNoYear : true,
    };
}

// ── URL ──────────────────────────────────────────────────────────────────

const splitList = (value) => String(value || '').split(',').map((s) => s.trim()).filter(Boolean);
const unique = (list) => [...new Set(list)];

/**
 * Read ?make=, ?model=, ?year= and ?noyear= into filter values. Makes and
 * models the inventory does not have are dropped. Returns null when none of
 * the params is present.
 */
export function parseMachineParams(search, catalog) {
    const params = new URLSearchParams(search || '');
    if (!MACHINE_URL_PARAMS.some((p) => params.has(p))) return null;
    const out = {};

    if (params.has('make')) {
        out.makes = unique(splitList(params.get('make')).map(canonicalMakeKey))
            .filter((key) => catalog.makes.has(key));
    }
    if (params.has('model')) {
        const makeSet = new Set(out.makes || []);
        const ids = [];
        splitList(params.get('model')).forEach((name) => {
            const modelKey = canonicalModelKey(name);
            catalog.models.forEach((m) => {
                if (m.id.split(ID_SEPARATOR)[1] !== modelKey) return;
                if (makeSet.size > 0 && !makeSet.has(m.makeKey)) return;
                ids.push(m.id);
            });
        });
        out.models = unique(ids);
    }
    if (params.has('year')) {
        const raw = params.get('year').trim();
        const range = raw.match(/^(\d{4})?-(\d{4})?$/);
        const single = raw.match(/^(\d{4})$/);
        if (single) {
            out.yearFrom = Number(single[1]);
            out.yearTo = Number(single[1]);
        } else if (range && (range[1] || range[2])) {
            out.yearFrom = range[1] ? Number(range[1]) : null;
            out.yearTo = range[2] ? Number(range[2]) : null;
        }
    }
    if (params.get('noyear') === '0') out.includeNoYear = false;
    else if (params.get('noyear') === '1') out.includeNoYear = true;
    return out;
}

// A label goes into the URL when it reads back to the same key and holds no
// list separator; otherwise the (idempotent) key does.
const urlToken = (label, key, keyOf) => (
    label && !label.includes(',') && keyOf(label) === key ? label : key
);

/** Write the machine filters into `params` (URLSearchParams), removing cleared ones. */
export function writeMachineParams(params, filters, catalog) {
    MACHINE_URL_PARAMS.forEach((p) => params.delete(p));
    const makes = listOf(filters?.makes);
    const models = listOf(filters?.models);
    if (makes.length > 0) {
        params.set('make', makes.map((key) => urlToken(catalog.makes.get(key)?.label, key, canonicalMakeKey)).join(','));
    }
    if (models.length > 0) {
        params.set('model', unique(models.map((id) => {
            const modelKey = String(id).split(ID_SEPARATOR)[1] || UNKNOWN_KEY;
            return urlToken(catalog.models.get(id)?.label, modelKey, canonicalModelKey);
        })).join(','));
    }
    if (filters?.yearFrom != null || filters?.yearTo != null) {
        params.set('year', `${filters.yearFrom ?? ''}-${filters.yearTo ?? ''}`);
    }
    if (filters?.includeNoYear === false) params.set('noyear', '0');
    return params;
}
